/**
 * Review-panel state for the NCCN evaluation screen.
 *
 * Owns a mutable draft copy of the loaded {@link NCCNEvaluationPayload}, the
 * guideline/criteria pager indices, and the persistence handlers. Feedback is
 * applied immutably via {@link applyItemFeedback} and persisted through
 * `submitNccnEvaluation` via {@link persistNccnReview}. Guideline-verdict
 * commits await recompute before flipping `review_submitted`, so a disagree
 * cannot appear as Agreed on the worklist while `ai_review_refuted` is still
 * false.
 * @module useNccnReview
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getNccnEvaluation } from "../../../api/postCall/nccnEvaluation";
import {
  LoadedNccnEvaluation,
  NCCNEvaluationPayload,
  NCCNEvaluationRecord,
} from "../../../data-model/nccnEvaluation";
import {
  EvaluationItem,
  EvaluationResult,
  HumanVerdict,
} from "../../../data-model/nccnGuidelines";
import { RootState } from "../../../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../../store/UserGeneralInfoStore";
import { logError } from "../../../utils/customLogger";
import { fromGetResponse } from "../utils/loadEvaluationPayload";
import {
  applyGuidelineReview,
  applyItemFeedback,
  canSubmitMnReview,
  computeMandatoryReviewProgress,
  FeedbackInput,
  findFirstUnreviewedMandatoryStep,
  findNextSkipTargetIndex,
  getMandatoryGuidelineIndices,
  GuidelineReviewInput,
  readGuidelineReview,
} from "../utils/nccnReviewFeedback";
import {
  NccnRecomputeError,
  persistNccnReview,
} from "../utils/persistNccnReview";

/** Arguments for {@link useNccnReview}. */
export interface UseNccnReviewArgs {
  /** The loaded evaluation (payload + envelope flags), or null while absent. */
  evaluation: LoadedNccnEvaluation | null;
  /** Order the evaluation belongs to. */
  orderId: string;
  /** Patient MRN, when known. */
  patientId?: string;
  /** Organisation ID for RLS-scoped writes, when known. */
  orgId?: string;
}

const EMPTY_PAYLOAD: NCCNEvaluationPayload = { guidelines: [] };

const clamp = (value: number, max: number): number =>
  Math.min(Math.max(value, 0), Math.max(max, 0));

/**
 * Manages the NCCN review panel's draft, navigation, and persistence.
 *
 * Feedback edits (`markCorrect` / `saveFeedback` on criteria and
 * `updateGuidelineReview` on the summary step) mutate the local draft only and
 * mark it dirty; nothing is sent to the backend until `commitDraft` runs — the
 * panel calls it on each navigation, so one submit batches all edits made on
 * the current step.
 *
 * Each guideline exposes one extra "summary" step after its last criterion
 * (`isSummaryStep`), on which the reviewer records the guideline-level verdict
 * via `updateGuidelineReview`.
 *
 * @param args - Loaded evaluation plus order/patient/org identifiers
 * @returns Draft state, pager indices, derived current guideline/item, the
 *   `goStep` cross-guideline navigator with its `isFirstStep` / `isLastStep` /
 *   `isSummaryStep` bounds, the `markCorrect` / `saveFeedback` handlers, the
 *   guideline-review `currentReview` /
 *   `updateGuidelineReview` pair, the `commitDraft` persistence trigger, the
 *   awaitable `submitReview` (force-persist the draft; returns whether save
 *   succeeded) and `refetch` (reload the stored evaluation into the draft),
 *   plus envelope `orderResult` / `aiEvaluation` / `reviewSubmitted` /
 *   `aiReviewRefuted`. `review_submitted` is stamped after recompute so a
 *   disagree cannot appear as Agreed on the worklist (sticky once true).
 */
export const useNccnReview = ({
  evaluation,
  orderId,
  patientId,
  orgId,
}: UseNccnReviewArgs) => {
  const payload = evaluation?.payload ?? null;
  const [draft, setDraft] = useState<NCCNEvaluationPayload>(
    payload ?? EMPTY_PAYLOAD,
  );
  const [reviewSubmitted, setReviewSubmitted] = useState(
    () => evaluation?.review_submitted ?? false,
  );
  const [currentOrderResult, setCurrentOrderResult] = useState<
    EvaluationResult | null | undefined
  >(() => evaluation?.order_evaluation_result);
  const [aiEvaluation, setAiEvaluation] = useState<
    EvaluationResult | null | undefined
  >(() => evaluation?.ai_evaluation);
  const [aiReviewRefuted, setAiReviewRefuted] = useState(
    () => evaluation?.ai_review_refuted ?? false,
  );
  const [guidelineIndex, setGuidelineIndex] = useState(
    () => findFirstUnreviewedMandatoryStep(payload).guidelineIndex,
  );
  const [criteriaIndex, setCriteriaIndex] = useState(
    () => findFirstUnreviewedMandatoryStep(payload).criteriaIndex,
  );
  // Full-array index of the single guideline being reviewed via the
  // "Override to Met" flow, or null during the normal (AI-Met) step-through.
  const [overrideIndex, setOverrideIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const draftDirty = useRef(false);
  const reviewerEmail = useSelector(
    (state: RootState) => state.firebaseAuthentication?.user?.email ?? "",
  );

  useEffect(() => {
    const nextPayload = evaluation?.payload ?? EMPTY_PAYLOAD;
    setDraft(nextPayload);
    setReviewSubmitted(evaluation?.review_submitted ?? false);
    setCurrentOrderResult(evaluation?.order_evaluation_result);
    setAiEvaluation(evaluation?.ai_evaluation);
    setAiReviewRefuted(evaluation?.ai_review_refuted ?? false);
    const resumeStep = findFirstUnreviewedMandatoryStep(
      evaluation?.payload ?? null,
    );
    setGuidelineIndex(resumeStep.guidelineIndex);
    setCriteriaIndex(resumeStep.criteriaIndex);
    setOverrideIndex(null);
    draftDirty.current = false;
  }, [evaluation]);

  const guidelines = useMemo<NCCNEvaluationRecord[]>(
    () => draft.guidelines ?? [],
    [draft],
  );
  const currentGuideline = guidelines[guidelineIndex];
  const items = useMemo<EvaluationItem[]>(
    () => currentGuideline?.metadata?.evaluation?.items ?? [],
    [currentGuideline],
  );
  const currentItem = items[criteriaIndex];

  // Indices (into the full guidelines array) that make up the mandatory
  // step-through: the AI-Met and AI-Undetermined guidelines.
  const mandatoryIndices = useMemo(
    () => getMandatoryGuidelineIndices(draft),
    [draft],
  );
  const isOverrideMode = overrideIndex !== null;

  // The current guideline's 1-based position on the mandatory step-through
  // track (AI-Met then AI-Undetermined) and the track length. Unlike the
  // grouped display position, this advances monotonically with each Next/Skip,
  // so the header counter never appears to stall or jump when a verdict
  // reshuffles the summary grouping. In override mode the current guideline is
  // off-track (an AI-Not-Met guideline), so the position is 0 and the panel
  // falls back to the grouped display position to match the summary row it was
  // opened from.
  const mandatoryTrackPosition = useMemo(() => {
    const position = mandatoryIndices.indexOf(guidelineIndex);
    return position === -1 ? 0 : position + 1;
  }, [guidelineIndex, mandatoryIndices]);
  const mandatoryTrackTotal = mandatoryIndices.length;

  /**
   * Enters the scoped single-guideline "Override to Met" review for the given
   * guideline, starting at its first criterion.
   *
   * @param index - Full-array index of the guideline to override
   */
  const beginOverride = useCallback(
    (index: number) => {
      const clamped = clamp(index, guidelines.length - 1);
      setOverrideIndex(clamped);
      setGuidelineIndex(clamped);
      setCriteriaIndex(0);
    },
    [guidelines.length],
  );

  /**
   * Exits the scoped override review and snaps the pager cursor back onto the
   * mandatory (AI-Met) track. Without this the cursor would stay on the
   * overridden Not-Met guideline (which is not in `mandatoryIndices`), so
   * re-entering the step-through afterwards would render an off-track guideline
   * and make `goStep`'s `indexOf`-based navigation misbehave.
   */
  const endOverride = useCallback(() => {
    setOverrideIndex(null);
    const resumeStep = findFirstUnreviewedMandatoryStep(draft);
    setGuidelineIndex(resumeStep.guidelineIndex);
    setCriteriaIndex(resumeStep.criteriaIndex);
  }, [draft]);

  const isReviewComplete = useMemo(
    () => computeMandatoryReviewProgress(draft).isComplete,
    [draft],
  );

  // Early-submit gate: one Human-Met guideline (or every mandatory guideline
  // Human Not Met) lets the reviewer jump straight to the review screen.
  const canSubmitReview = useMemo(() => canSubmitMnReview(draft), [draft]);

  // Whether the current guideline is not the last one on the mandatory track;
  // used to disable "Skip to next guideline" when there is nowhere to go.
  const hasNextMandatoryGuideline = useMemo(() => {
    if (isOverrideMode) return false;
    const position = mandatoryIndices.indexOf(guidelineIndex);
    return position !== -1 && position < mandatoryIndices.length - 1;
  }, [guidelineIndex, isOverrideMode, mandatoryIndices]);

  // While reviewing a guideline from the summary (override mode), the next
  // guideline to chain a skip to: the next unreviewed guideline in the same
  // AI group (AI-Met, or AI-Not-Met / Undetermined) as the current one, or
  // null when none remain (a skip then returns to the summary; the groups
  // never cross over). Null outside override mode, where the mandatory track
  // drives the advance.
  const nextSkipTargetIndex = useMemo(
    () =>
      isOverrideMode ? findNextSkipTargetIndex(draft, guidelineIndex) : null,
    [draft, guidelineIndex, isOverrideMode],
  );

  const goStep = useCallback(
    (delta: number) => {
      const position = mandatoryIndices.indexOf(guidelineIndex);
      if (delta > 0) {
        if (criteriaIndex < items.length) {
          setCriteriaIndex((prev) => prev + 1);
        } else if (!isOverrideMode && position < mandatoryIndices.length - 1) {
          setGuidelineIndex(mandatoryIndices[position + 1]);
          setCriteriaIndex(0);
        }
        return;
      }
      if (criteriaIndex > 0) {
        setCriteriaIndex((prev) => prev - 1);
      } else if (!isOverrideMode && position > 0) {
        const prevIndex = mandatoryIndices[position - 1];
        const prevItems =
          guidelines[prevIndex]?.metadata?.evaluation?.items ?? [];
        setGuidelineIndex(prevIndex);
        setCriteriaIndex(prevItems.length);
      }
    },
    [
      criteriaIndex,
      guidelineIndex,
      guidelines,
      isOverrideMode,
      items.length,
      mandatoryIndices,
    ],
  );

  const isSummaryStep = criteriaIndex >= items.length;
  const isFirstStep = isOverrideMode
    ? criteriaIndex <= 0
    : guidelineIndex === mandatoryIndices[0] && criteriaIndex <= 0;
  const isLastStep = isOverrideMode
    ? criteriaIndex >= items.length
    : guidelineIndex === mandatoryIndices[mandatoryIndices.length - 1] &&
      criteriaIndex >= items.length;

  /**
   * Persists the draft via {@link persistNccnReview} and writes envelope flags
   * from the result. `currentOrderResult` and `aiReviewRefuted` must be in
   * the dependency list so a criteria-step save after recompute does not
   * echo stale pre-recompute values.
   *
   * @param nextDraft - Draft payload to write
   * @param shouldRecompute - Whether to recompute the order-level verdict
   * @param forceSubmitted - Stamp `review_submitted` regardless of mandatory
   *   progress; used by the terminal submit only
   * @returns Whether the save succeeded
   */
  const persist = useCallback(
    async (
      nextDraft: NCCNEvaluationPayload,
      shouldRecompute: boolean,
      forceSubmitted = false,
    ): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        const result = await persistNccnReview({
          orderId,
          patientId,
          orgId,
          userId: UserStore.getItem(UserGeneralInfoContextKeys.USER_ID) ?? "",
          nextDraft,
          alreadySubmitted: reviewSubmitted,
          shouldRecompute,
          currentOrderResult,
          currentAiReviewRefuted: aiReviewRefuted,
          forceSubmitted,
        });
        setDraft(result.payload);
        setReviewSubmitted(result.reviewSubmitted);
        setCurrentOrderResult(result.orderEvaluationResult);
        setAiReviewRefuted(result.aiReviewRefuted);
        toast.success("Feedback saved");
        return true;
      } catch (error) {
        logError(error as Error, "Error saving NCCN evaluation feedback");
        toast.error(
          error instanceof NccnRecomputeError
            ? "Feedback saved, but the order-level result could not be updated. Try again."
            : "Failed to save feedback",
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      orderId,
      patientId,
      orgId,
      reviewSubmitted,
      currentOrderResult,
      aiReviewRefuted,
    ],
  );

  const applyItemFeedbackLocal = useCallback(
    (input: FeedbackInput) => {
      const userId =
        UserStore.getItem(UserGeneralInfoContextKeys.USER_ID) ?? "";
      draftDirty.current = true;
      setDraft((prev) =>
        applyItemFeedback(prev, guidelineIndex, criteriaIndex, input, userId),
      );
    },
    [guidelineIndex, criteriaIndex],
  );

  const markCorrect = useCallback(
    () => applyItemFeedbackLocal({ type: "correct" }),
    [applyItemFeedbackLocal],
  );

  const saveFeedback = useCallback(
    (input: FeedbackInput) => applyItemFeedbackLocal(input),
    [applyItemFeedbackLocal],
  );

  const currentReview = useMemo(
    () => readGuidelineReview(currentGuideline),
    [currentGuideline],
  );

  const updateGuidelineReview = useCallback(
    (input: GuidelineReviewInput) => {
      draftDirty.current = true;
      setDraft((prev) =>
        applyGuidelineReview(prev, guidelineIndex, input, reviewerEmail),
      );
    },
    [guidelineIndex, reviewerEmail],
  );

  /**
   * Skips the current guideline: records a "Not Met" human verdict with the
   * reviewer-selected reason as its `verdict_reasoning`, persists it like a
   * guideline-verdict commit (so order-level recompute runs), then advances
   * the pager to the next mandatory guideline's first criterion. In override
   * mode (a Not Met / Undetermined guideline opened from the summary) this
   * does not advance the pager — the caller chains to the next not-met
   * guideline or ends the override and returns to the summary — and
   * `review_submitted` is only forced on the last mandatory guideline, since
   * one skipped off-track guideline does not conclude the mandatory track.
   *
   * @param reasoning - The selected skip reason, stored as `verdict_reasoning`
   * @returns Whether the skip was persisted (false leaves the pager in place)
   */
  const skipToNextGuideline = useCallback(
    async (reasoning: string): Promise<boolean> => {
      const position = mandatoryIndices.indexOf(guidelineIndex);
      const isLastMandatory =
        position === mandatoryIndices.length - 1 && position !== -1;
      // In override mode the caller (panel action) chains to the next not-met
      // guideline or ends the override, so no pager advance happens here.
      const nextGuidelineIndex = isLastMandatory
        ? undefined
        : position !== -1 && !isOverrideMode
          ? mandatoryIndices[position + 1]
          : undefined;
      const nextDraft = applyGuidelineReview(
        draft,
        guidelineIndex,
        {
          ...currentReview,
          verdict: HumanVerdict.NotMet,
          verdictReasoning: reasoning,
        },
        reviewerEmail,
      );
      // Skipping the last mandatory guideline concludes every mandatory
      // guideline (prior ones already carry a verdict), so this is a terminal
      // submit and the reviewer should land on the review screen. `forceSubmitted`
      // matches `submitReview`, since mandatory progress can never be "complete"
      // when every mandatory guideline is Not Met.
      const saved = await persist(nextDraft, true, isLastMandatory);
      if (!saved) return false;
      draftDirty.current = false;
      if (nextGuidelineIndex !== undefined) {
        setGuidelineIndex(nextGuidelineIndex);
        setCriteriaIndex(0);
      }
      return true;
    },
    [
      currentReview,
      draft,
      guidelineIndex,
      isOverrideMode,
      mandatoryIndices,
      persist,
      reviewerEmail,
    ],
  );

  const commitDraft = useCallback(async (): Promise<boolean> => {
    if (!draftDirty.current) return true;
    // Only the summary step commits a guideline-level verdict, so recompute
    // there; criteria-step saves must not trigger it. `review_submitted` is
    // deferred inside persist until recompute writes `ai_review_refuted`.
    const saved = await persist(draft, isSummaryStep);
    if (saved) draftDirty.current = false;
    return saved;
  }, [persist, draft, isSummaryStep]);

  const submitReview = useCallback(async (): Promise<boolean> => {
    // Terminal submit: force the flag rather than deriving it from mandatory
    // progress, which is never complete when the AI marked every guideline
    // Not Met (no mandatory guidelines, so the override flow is the whole
    // review). The flag is still deferred inside persist until recompute
    // writes `ai_review_refuted`.
    const saved = await persist(draft, true, true);
    if (saved) draftDirty.current = false;
    return saved;
  }, [persist, draft]);

  const refetch = useCallback(async (): Promise<void> => {
    try {
      const response = await getNccnEvaluation({
        order_id: orderId,
        org_id: orgId ?? null,
      });
      const loaded = fromGetResponse(response);
      if (loaded) {
        setDraft(loaded.payload);
        setReviewSubmitted(loaded.review_submitted);
        setCurrentOrderResult(loaded.order_evaluation_result);
        setAiEvaluation(loaded.ai_evaluation);
        setAiReviewRefuted(loaded.ai_review_refuted);
        draftDirty.current = false;
      }
    } catch (error) {
      logError(error as Error, "Error reloading NCCN evaluation");
    }
  }, [orderId, orgId]);

  return {
    draft,
    guidelines,
    guidelineIndex,
    criteriaIndex,
    guidelineCount: guidelines.length,
    criteriaCount: items.length,
    currentGuideline,
    currentItem,
    mandatoryTrackPosition,
    mandatoryTrackTotal,
    orderResult: currentOrderResult,
    aiEvaluation,
    reviewSubmitted,
    aiReviewRefuted,
    isReviewComplete,
    canSubmitReview,
    hasNextMandatoryGuideline,
    nextSkipTargetIndex,
    isSubmitting,
    isOverrideMode,
    beginOverride,
    endOverride,
    goStep,
    isFirstStep,
    isLastStep,
    isSummaryStep,
    markCorrect,
    saveFeedback,
    currentReview,
    updateGuidelineReview,
    skipToNextGuideline,
    commitDraft,
    submitReview,
    refetch,
  };
};
